import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { Separator } from "@/client/components/ui/separator";
import { homePathForUser, useAuthStore } from "@/client/features/auth/auth-store";
import { ApiError } from "@/client/lib/api";
import { MfaChallenge } from "@/client/pages/auth/mfa-challenge";
import { DEV_LOGIN } from "@/shared/dev-auth";
import type { UserKind } from "@/shared/types";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

type Values = z.infer<typeof schema>;

export function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const mockSignIn = useAuthStore((s) => s.mockSignIn);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? null;
  const [mfa, setMfa] = useState<{ enrolling: boolean } | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (values: Values) => {
    try {
      const outcome = await login(values);
      if (outcome.kind === "mfa") {
        setMfa({ enrolling: outcome.challenge.enrolling });
        return;
      }
      navigate(from ?? homePathForUser(outcome.user), { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Unable to sign in. Please try again.";
      toast.error(message);
    }
  };

  // Dev shortcut: sign in for real against the seeded accounts (see
  // scripts/dev/seed.ts) so data-backed pages work. A 401 means the DB isn't
  // seeded (no such account), so fall back to the client-only mock session to
  // keep the shells navigable; any other failure is a real error, so surface it
  // rather than silently dropping into a data-less mock session.
  const devSignIn = async (kind: UserKind) => {
    const creds = kind === "staff" ? DEV_LOGIN.staff : DEV_LOGIN.customer;
    try {
      const outcome = await login(creds);
      if (outcome.kind === "mfa") {
        setMfa({ enrolling: outcome.challenge.enrolling });
        return;
      }
      navigate(homePathForUser(outcome.user), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) {
        toast.message("Using a mock session - run `make db-seed` for data-backed dev login.");
        mockSignIn(kind);
        navigate(kind === "staff" ? "/admin" : "/", { replace: true });
        return;
      }
      const message =
        err instanceof ApiError ? err.message : "Unable to sign in. Please try again.";
      toast.error(message);
    }
  };

  if (mfa) {
    return (
      <MfaChallenge
        enrolling={mfa.enrolling}
        onComplete={(user) => navigate(from ?? homePathForUser(user), { replace: true })}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sign in</CardTitle>
        <CardDescription>Access your Mantle portal account.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Password</FormLabel>
                    <Link
                      to="/forgot-password"
                      className="text-muted-foreground hover:text-foreground text-xs"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <FormControl>
                    <Input type="password" autoComplete="current-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </Form>

        {import.meta.env.DEV ? (
          <div className="grid gap-2">
            <div className="flex items-center gap-3">
              <Separator className="flex-1" />
              <span className="text-muted-foreground text-xs">dev shortcuts</span>
              <Separator className="flex-1" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  void devSignIn("customer");
                }}
              >
                As customer
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  void devSignIn("staff");
                }}
              >
                As staff
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
